import type { ReactElement, ReactNode } from 'react';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

export interface StepFormChromeProps {
  step: number;
  totalSteps: number;
  stepLabel: string;
  children: ReactNode;
  error?: string | null;
  backLabel?: string;
  nextLabel?: string;
  submitLabel?: string;
  canGoNext?: boolean;
  isSubmitting?: boolean;
  onBack?: () => void;
  onNext?: () => void;
  onSubmit?: () => void;
  isLastStep: boolean;
}

export function StepFormChrome({
  step,
  totalSteps,
  stepLabel,
  children,
  error = null,
  backLabel,
  nextLabel,
  submitLabel,
  canGoNext = true,
  isSubmitting = false,
  onBack,
  onNext,
  onSubmit,
  isLastStep,
}: StepFormChromeProps): ReactElement {
  return (
    <div className="step-form">
      <div className="step-form__progress" aria-hidden>
        {Array.from({ length: totalSteps }, (_, index) => (
          <span
            key={index}
            className={
              index <= step
                ? 'step-form__dot step-form__dot--active'
                : 'step-form__dot'
            }
          />
        ))}
      </div>
      <p className="step-form__meta">
        {t('common.forms.stepOf')
          .replace('{{step}}', String(step + 1))
          .replace('{{total}}', String(totalSteps))}
        <span className="step-form__meta-label">{stepLabel}</span>
      </p>

      <div className="step-form__body">{children}</div>

      {error ? <p className="fm-error">{error}</p> : null}

      <div className="step-form__actions">
        {step > 0 && onBack ? (
          <Button
            label={backLabel ?? t('common.forms.back')}
            variant="ghost"
            disabled={isSubmitting}
            onClick={onBack}
          />
        ) : (
          <span />
        )}
        {isLastStep ? (
          <Button
            label={submitLabel ?? t('common.forms.submit')}
            disabled={isSubmitting || !canGoNext}
            onClick={onSubmit}
          />
        ) : (
          <Button
            label={nextLabel ?? t('common.forms.next')}
            disabled={!canGoNext || isSubmitting}
            onClick={onNext}
          />
        )}
      </div>
    </div>
  );
}
